import * as THREE from 'three';
import type { TemporalUniforms } from '../core/types';

export function createTemporalUniforms(): TemporalUniforms {
  return {
    uTime: { value: 0 },
    uMemory: { value: 0.15 },
    uResonance: { value: 0 },
    uPulseOrigin: { value: new THREE.Vector3(0, 6, 0) },
    uTelescopeFocus: { value: 0 },
  };
}

/**
 * 1. Celestial Twilight-to-Memory Sky Dome Shader
 */
export function createSkyDomeMaterial(uniforms: TemporalUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      uTime: uniforms.uTime,
      uMemory: uniforms.uMemory,
      uResonance: uniforms.uResonance,
      uTelescopeFocus: uniforms.uTelescopeFocus,
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      varying vec3 vDir;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        vDir = normalize(position);
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uMemory;
      uniform float uResonance;
      uniform float uTelescopeFocus;
      varying vec3 vWorldPos;
      varying vec3 vDir;

      float hash31(vec3 p) {
        p = fract(p * vec3(443.897, 441.423, 437.195));
        p += dot(p, p.yzx + 19.19);
        return fract((p.x + p.y) * p.z);
      }

      void main() {
        vec3 d = normalize(vDir);
        float elev = clamp(d.y, -0.25, 1.0);
        float h = clamp(elev, 0.0, 1.0);

        vec3 desireHorizon = vec3(0.84, 0.42, 0.22);
        vec3 desireLow     = vec3(0.44, 0.20, 0.35);
        vec3 desireMid     = vec3(0.15, 0.10, 0.25);
        vec3 desireZenith  = vec3(0.05, 0.05, 0.12);

        vec3 memoryHorizon = vec3(0.17, 0.25, 0.40);
        vec3 memoryLow     = vec3(0.09, 0.14, 0.28);
        vec3 memoryMid     = vec3(0.05, 0.08, 0.17);
        vec3 memoryZenith  = vec3(0.02, 0.03, 0.08);

        vec3 horizon = mix(desireHorizon, memoryHorizon, uMemory);
        vec3 lowSky  = mix(desireLow, memoryLow, uMemory);
        vec3 midSky  = mix(desireMid, memoryMid, uMemory);
        vec3 zenith  = mix(desireZenith, memoryZenith, uMemory);

        vec3 col = mix(horizon, lowSky, smoothstep(0.0, 0.14, h));
        col = mix(col, midSky, smoothstep(0.12, 0.42, h));
        col = mix(col, zenith, smoothstep(0.38, 0.92, h));

        vec3 sunDir = normalize(vec3(-0.62, 0.16 - uMemory * 0.05, -0.76));
        float sunAmt = max(dot(d, sunDir), 0.0);
        vec3 sunGlowColor = mix(vec3(0.95, 0.58, 0.26), vec3(0.45, 0.65, 0.92), uMemory);
        col += sunGlowColor * pow(sunAmt, 7.0) * (0.42 - 0.16 * uMemory);
        col += sunGlowColor * pow(sunAmt, 48.0) * 0.25;

        if (d.y > 0.02) {
          vec3 starGrid = floor(d * 260.0);
          float r = hash31(starGrid);
          if (r > 0.992) {
            vec3 cellCenter = (starGrid + 0.5) / 260.0;
            float dist = length(d - normalize(cellCenter)) * 260.0;
            float twinkle = 0.65 + 0.35 * sin(uTime * (2.0 + r * 6.0) + r * 90.0);
            float starVis = smoothstep(0.02, 0.28, d.y) * mix(0.65, 1.2, uMemory);
            float intensity = smoothstep(0.48, 0.05, dist) * twinkle * starVis;
            vec3 starTint = mix(vec3(1.0, 0.92, 0.78), vec3(0.78, 0.88, 1.0), fract(r * 17.3));
            col += starTint * intensity * 1.15;
          }
        }

        float polarAngle = acos(clamp(d.y, -1.0, 1.0));
        float azimuth = atan(d.z, d.x);
        float ringPattern = abs(sin(polarAngle * 18.0));
        float meridianPattern = abs(sin(azimuth * 6.0 + uTime * 0.02));
        float gridLine = smoothstep(0.018, 0.0, ringPattern) + smoothstep(0.012, 0.0, meridianPattern) * 0.6;
        float astroVisibility = smoothstep(0.15, 0.55, d.y) * (0.03 + 0.16 * uTelescopeFocus + 0.05 * uMemory + 0.06 * uResonance);
        vec3 astroColor = mix(vec3(0.92, 0.75, 0.42), vec3(0.52, 0.78, 0.96), uMemory);
        col += astroColor * gridLine * astroVisibility;

        if (d.y < 0.0) {
          vec3 abyss = mix(vec3(0.16, 0.09, 0.16), vec3(0.04, 0.07, 0.14), uMemory);
          col = mix(horizon * 0.65, abyss, smoothstep(0.0, -0.18, d.y));
        }

        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
}

/**
 * 2. Reflective Twilight Lagoon Water Shader
 * Mirrors the twilight/nocturnal horizon with gentle concentric ripples and warm city reflections.
 */
export function createLagoonWaterMaterial(uniforms: TemporalUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: uniforms.uTime,
      uMemory: uniforms.uMemory,
      uResonance: uniforms.uResonance,
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uMemory;
      uniform float uResonance;
      varying vec3 vWorldPos;

      void main() {
        float dist = length(vWorldPos.xz);
        vec3 V = normalize(cameraPosition - vWorldPos);

        // Concentric lagoon ripples around the spiral island
        float wave1 = sin(dist * 1.4 - uTime * 1.6 + atan(vWorldPos.z, vWorldPos.x) * 2.0);
        float wave2 = cos(vWorldPos.x * 0.8 + vWorldPos.z * 0.9 - uTime * 1.1);
        float ripple = (wave1 * 0.6 + wave2 * 0.4) * (0.04 + 0.04 * uResonance);

        vec3 N = normalize(vec3(ripple, 1.0, ripple));
        float fresnel = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 2.8);

        vec3 desireDeep = vec3(0.10, 0.06, 0.13);
        vec3 desireRefl = vec3(0.58, 0.28, 0.24);
        vec3 memoryDeep = vec3(0.03, 0.05, 0.11);
        vec3 memoryRefl = vec3(0.14, 0.24, 0.42);

        vec3 deepCol = mix(desireDeep, memoryDeep, uMemory);
        vec3 reflCol = mix(desireRefl, memoryRefl, uMemory);

        vec3 col = mix(deepCol, reflCol, fresnel * 0.78);

        // Warm golden / lunar reflection ring surrounding the citadel base (r = 18..36)
        float cityGlowRing = smoothstep(42.0, 18.0, dist) * smoothstep(12.0, 21.0, dist);
        float shimmer = 0.65 + 0.35 * sin(dist * 3.2 - uTime * 2.8 + wave1 * 2.0);
        vec3 cityReflTint = mix(vec3(0.92, 0.52, 0.22), vec3(0.38, 0.68, 0.96), uMemory);
        col += cityReflTint * cityGlowRing * shimmer * 0.28;

        // Smooth distance fade into the horizon mist
        vec3 horizonMist = mix(vec3(0.42, 0.21, 0.26), vec3(0.11, 0.17, 0.30), uMemory);
        col = mix(col, horizonMist, smoothstep(45.0, 165.0, dist));

        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
}

/**
 * 3. Iridescent Mother-of-Pearl / Nacre Seashell Shader
 */
export function createNacreMaterial(
  uniforms: TemporalUniforms,
  accentHue = 0.0
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: uniforms.uTime,
      uMemory: uniforms.uMemory,
      uResonance: uniforms.uResonance,
      uPulseOrigin: uniforms.uPulseOrigin,
      uAccentHue: { value: accentHue },
      uHover: { value: 0.0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vWorldPos;
      varying vec2 vUv;

      void main() {
        vUv = uv;
        mat4 localMatrix = modelMatrix;
        #ifdef USE_INSTANCING
          localMatrix = modelMatrix * instanceMatrix;
        #endif
        vNormal = normalize(mat3(localMatrix) * normal);
        vec4 wp = localMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uMemory;
      uniform float uResonance;
      uniform vec3 uPulseOrigin;
      uniform float uAccentHue;
      uniform float uHover;

      varying vec3 vNormal;
      varying vec3 vWorldPos;
      varying vec2 vUv;

      vec3 pearlNacreTint(float cosTheta, float shift) {
        vec3 warmPearl = vec3(0.94, 0.85, 0.74);
        vec3 roseGold  = vec3(0.92, 0.74, 0.72);
        vec3 aquaOpal  = vec3(0.72, 0.86, 0.92);
        float t1 = 0.5 + 0.5 * cos(6.28318 * (cosTheta * 1.4 + 0.15) + shift);
        float t2 = 0.5 + 0.5 * sin(6.28318 * (cosTheta * 1.9 - 0.1) + shift * 0.7);
        vec3 c = mix(warmPearl, roseGold, t1 * 0.55);
        return mix(c, aquaOpal, t2 * (0.28 + 0.35 * uMemory));
      }

      void main() {
        vec3 N = normalize(vNormal);
        vec3 V = normalize(cameraPosition - vWorldPos);
        float ndotv = clamp(abs(dot(N, V)), 0.0, 1.0);
        float fresnel = pow(1.0 - ndotv, 2.4);

        float ribs = 0.5 + 0.5 * sin(vUv.x * 84.0);
        float whorlShadow = 0.72 + 0.28 * sin(vUv.y * 6.28318);

        vec3 desireBase = vec3(0.84, 0.71, 0.58);
        vec3 memoryBase = vec3(0.58, 0.69, 0.84);
        vec3 baseColor = mix(desireBase, memoryBase, uMemory) * whorlShadow;

        vec3 keyDir1 = normalize(vec3(0.55, 0.75, 0.65));
        vec3 keyDir2 = normalize(vec3(-0.5, 0.3, -0.6));
        float diff1 = max(dot(N, keyDir1), 0.0);
        float diff2 = max(dot(N, keyDir2), 0.0);

        vec3 H = normalize(keyDir1 + V);
        float spec = pow(max(dot(N, H), 0.0), 36.0);

        vec3 nacre = pearlNacreTint(ndotv + ribs * 0.08, uAccentHue + uMemory * 1.1);

        vec3 lit = baseColor * (0.28 + 0.56 * diff1 + 0.22 * diff2);
        lit = mix(lit, nacre * (0.45 + 0.48 * diff1), 0.42 + 0.38 * fresnel);
        lit *= (0.85 + 0.18 * ribs);
        lit += vec3(0.98, 0.90, 0.78) * spec * 0.28;

        float dist = length(vWorldPos - uPulseOrigin);
        float wave = (0.5 + 0.5 * sin(dist * 1.2 - uTime * 7.0)) * exp(-dist * 0.09);
        float pulseGlow = wave * uResonance;
        lit += mix(vec3(0.95, 0.72, 0.35), vec3(0.48, 0.82, 0.98), uMemory) * (pulseGlow * 0.28 + fresnel * 0.15 + uHover * 0.32);

        gl_FragColor = vec4(lit, 1.0);
      }
    `,
  });
}

/**
 * 4. Vibrating Resonant Violin String Shader
 */
export function createViolinStringMaterial(
  uniforms: TemporalUniforms,
  stringIndex = 0
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    uniforms: {
      uTime: uniforms.uTime,
      uMemory: uniforms.uMemory,
      uResonance: uniforms.uResonance,
      uExcite: { value: 0.12 },
      uFrequency: { value: 220.0 + stringIndex * 110.0 },
      uStringIndex: { value: stringIndex },
    },
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform float uExcite;
      uniform float uResonance;
      uniform float uFrequency;
      varying vec2 vUv;
      varying float vDisplacement;

      void main() {
        vUv = uv;
        float envelope = sin(uv.y * 3.14159265);
        float overtone = 0.35 * sin(uv.y * 6.2831853);
        float amp = (uExcite + uResonance * 0.45) * 0.085;
        float wave = (envelope * sin(uTime * 32.0 + uFrequency * 0.05) + overtone * cos(uTime * 54.0)) * amp;
        vDisplacement = abs(wave) / max(amp, 0.001);

        vec3 displaced = position + normal * wave;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uMemory;
      uniform float uExcite;
      uniform float uResonance;
      varying vec2 vUv;
      varying float vDisplacement;

      void main() {
        vec3 goldColor = vec3(0.95, 0.78, 0.44);
        vec3 silverBlue = vec3(0.68, 0.86, 0.98);
        vec3 base = mix(goldColor, silverBlue, uMemory * 0.7);

        float harmonicNodes = 0.78 + 0.22 * sin(vUv.y * 12.566 - uTime * 6.0);
        float energy = 0.76 + max(0.0, uExcite - 0.16) * 0.95 + uResonance * 0.45 + vDisplacement * 0.2;
        vec3 finalCol = base * energy * harmonicNodes;

        gl_FragColor = vec4(finalCol, 0.95);
      }
    `,
  });
}

/**
 * 5. Optical Telescope Starlight Cone Shader
 */
export function createStarbeamMaterial(uniforms: TemporalUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uTime: uniforms.uTime,
      uMemory: uniforms.uMemory,
      uTelescopeFocus: uniforms.uTelescopeFocus,
      uResonance: uniforms.uResonance,
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vWorldPos;
      void main() {
        vUv = uv;
        vNormal = normalize(mat3(modelMatrix) * normal);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uMemory;
      uniform float uTelescopeFocus;
      uniform float uResonance;
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vWorldPos;

      void main() {
        vec3 V = normalize(cameraPosition - vWorldPos);
        float fresnel = pow(1.0 - abs(dot(normalize(vNormal), V)), 1.8);
        float axialFade = pow(1.0 - vUv.y, 1.6) * smoothstep(0.0, 0.15, vUv.y);
        float caustic = 0.75 + 0.25 * sin(vUv.y * 24.0 - uTime * 2.0 + vUv.x * 12.566);

        vec3 beamWarm = vec3(0.95, 0.78, 0.46);
        vec3 beamCool = vec3(0.48, 0.78, 0.98);
        vec3 col = mix(beamWarm, beamCool, 0.35 + 0.55 * uMemory);

        float alpha = axialFade * (0.18 + 0.55 * fresnel) * caustic * (0.09 + 0.16 * uTelescopeFocus + 0.05 * uResonance);
        gl_FragColor = vec4(col, clamp(alpha, 0.0, 0.32));
      }
    `,
  });
}

/**
 * 6. Carved Mediterranean Travertine & Terracotta Architecture Material
 */
export function createStoneMaterial(
  uniforms: TemporalUniforms,
  options: {
    baseDesireHex: number;
    baseMemoryHex: number;
    roughness?: number;
    metalness?: number;
    emissiveHex?: number;
    emissiveIntensity?: number;
  }
): THREE.MeshStandardMaterial {
  const desireColor = new THREE.Color(options.baseDesireHex);
  const memoryColor = new THREE.Color(options.baseMemoryHex);

  const mat = new THREE.MeshStandardMaterial({
    color: desireColor.clone(),
    roughness: options.roughness ?? 0.62,
    metalness: options.metalness ?? 0.15,
    emissive: new THREE.Color(options.emissiveHex ?? 0x22131c),
    emissiveIntensity: options.emissiveIntensity ?? 0.28,
  });

  mat.userData.desireColor = desireColor;
  mat.userData.memoryColor = memoryColor;
  mat.userData.uniforms = uniforms;

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uniforms.uTime;
    shader.uniforms.uMemory = uniforms.uMemory;
    shader.uniforms.uResonance = uniforms.uResonance;
    shader.uniforms.uPulseOrigin = uniforms.uPulseOrigin;

    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `#include <common>
       varying vec3 vStoneWorldPos;`
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
       vStoneWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
       uniform float uTime;
       uniform float uMemory;
       uniform float uResonance;
       uniform vec3 uPulseOrigin;
       varying vec3 vStoneWorldPos;`
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      `
       float strata = 0.93 + 0.07 * sin(vStoneWorldPos.y * 8.5 + vStoneWorldPos.x * 1.7);
       gl_FragColor.rgb *= strata;

       float plazaWarmth = smoothstep(15.0, -3.0, vStoneWorldPos.y) * (1.0 - 0.5 * uMemory);
       gl_FragColor.rgb += vec3(0.22, 0.09, 0.04) * plazaWarmth * 0.42;

       float pDist = length(vStoneWorldPos - uPulseOrigin);
       float ripple = (0.5 + 0.5 * sin(pDist * 0.95 - uTime * 6.0)) * exp(-pDist * 0.085) * uResonance;
       vec3 rippleColor = mix(vec3(0.95, 0.70, 0.32), vec3(0.45, 0.78, 0.98), uMemory);
       gl_FragColor.rgb += rippleColor * ripple * 0.22;

       #include <dithering_fragment>
      `
    );
  };

  return mat;
}
